/**
 * FCPX-startpakket (fase 1): teksten bij de statussen. Puur, zodat het getest kan worden.
 * De statussen zelf komen van de engine (services/startpakket/statussen.py).
 */

export const INGEST_LABELS = {
  project_kiezen: 'Wacht op project',
  map_zoeken: 'Projectmap zoeken op de NAS',
  map_kiezen: 'Kies de projectmap',
  map_aanmaken: 'Projectmap aanmaken',
  mappen_klaar: 'Mappen worden klaargezet',
  wacht_op_media: 'Wacht op beelden',
  deels_binnen: 'Deels binnen',
  controleren: 'Controleren',
  media_compleet: 'Media binnen',
};

export const MEDIA_STAPPEN = ['wacht_op_media', 'deels_binnen', 'controleren'];

export function ingestLabel(status) {
  return INGEST_LABELS[status] || status || 'Onbekend';
}

/** Kleur-semantiek: alleen 'klaar' en 'aandacht' krijgen kleur; de rest blijft rustig grijs. */
export function ingestToon(status) {
  if (status === 'media_compleet') return 'klaar';
  if (status === 'controleren' || status === 'map_kiezen' || status === 'map_aanmaken') return 'aandacht';
  return 'rustig';
}

export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1024 ** 4) return `${(b / 1024 ** 4).toFixed(1)} TB`;
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
  return `${b} B`;
}

/** De inventaris die "Alles binnen" eerst laat bevestigen (plan §3). */
export function inventarisTekst(job) {
  const bronnen = job?.bronnen || [];
  if (!bronnen.length) {
    return 'Er is nog geen OffShoot-log gevonden. Weet je zeker dat alles binnen is? Het startpakket wacht dan tot de beelden er zijn.';
  }
  const regels = bronnen.map((b) => {
    const staat = b.ok ? 'geverifieerd of geaccepteerd'
      : b.probleem ? `controleren (${b.logStatus || 'onbekend'}${b.ontbrekend ? `, ${b.ontbrekend} ontbrekend` : ''})`
        : 'compleet gekopieerd, niet geverifieerd';
    return `• ${b.naam || 'Bron'}: ${b.aantalBestanden} bestanden, ${formatBytes(b.totaalBytes)} — ${staat}`;
  });
  const verwacht = job?.verwachteBronnen ? `Verwacht: ${job.verwachteBronnen} bron(nen). ` : '';
  return `${verwacht}Gevonden:\n${regels.join('\n')}`;
}

/** Compleet gekopieerd maar niet geverifieerd: alleen die kan de PL accepteren (geen probleembronnen). */
export function onbewezen(job) {
  return (job?.bronnen || []).filter((b) => !b.ok && !b.probleem);
}

/** Fase 2/3: één regel over de clips, de transcripten en het pakket (alleen na "Media binnen"). */
export const TRANSCRIPTIE_LABELS = {
  wacht: 'Clips worden gelezen',
  niet_nodig: 'Geen interviews gevonden',
  bezig: 'Interviews worden getranscribeerd',
  klaar: 'Transcripten klaar',
  deels: 'Transcripten deels klaar',
  wacht_eu: 'Wacht op EU-verwerking',
  geblokkeerd: 'Transcriptietool niet goedgekeurd door de klant',
  overgeslagen: 'Transcriptie overgeslagen',
};

export function transcriptieLabel(status) {
  return TRANSCRIPTIE_LABELS[status] || status || '';
}

export function pakketTekst(job) {
  if (!job || job.ingestStatus !== 'media_compleet') return null;
  if (job.pakketStatus === 'klaar') return `Startpakket v${job.pakketVersie} klaar — edit kan starten`;
  if (job.pakketStatus === 'bezig') return `Startpakket v${job.pakketVersie || 1} wordt op de NAS gezet`;
  if (job.pakketStatus === 'fout') return 'Startpakket bouwen mislukt';
  return 'Startpakket volgt na de transcripten';
}

/** Kleur van de hele regel: groen pas als het pakket klaar is (fase 3), oranje bij een fout. */
export function jobToon(job) {
  if (!job || job.ingestStatus !== 'media_compleet') return ingestToon(job?.ingestStatus);
  if (job.pakketStatus === 'klaar') return 'klaar';
  if (job.pakketStatus === 'fout') return 'aandacht';
  return 'rustig';
}

/** De korte status in de ingeklapte regel: na "Media binnen" de stand van het pakket. */
export function kopTekst(job) {
  if (!job) return '';
  if (job.ingestStatus !== 'media_compleet') return ingestLabel(job.ingestStatus);
  if (job.pakketStatus === 'klaar') return 'Edit kan starten';
  if (job.pakketStatus === 'bezig') return 'Pakket wordt op de NAS gezet';
  if (job.detectieStatus !== 'klaar') return 'Clips worden gelezen';
  return transcriptieLabel(job.transcriptieStatus) || 'Media binnen';
}

export const SOORT_LABELS = { interview: 'Interview', twijfel: 'Interview?', broll: 'B-roll', audio: 'Losse audio' };

/** "125,5 s" → "2:05". */
export function duurTekst(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const u = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return u ? `${u}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

export function brugTekst(brug) {
  if (!brug || brug.status === 'online') return null;
  if (brug.status === 'geen') return 'Er is nog geen NAS-brug gekoppeld — het startpakket wacht in de wachtrij.';
  const sinds = brug.laatstGezien
    ? new Date(`${brug.laatstGezien}Z`).toLocaleString('nl-NL', { timeZone: 'Europe/Amsterdam', dateStyle: 'short', timeStyle: 'short' })
    : null;
  return `NAS-brug offline${sinds ? ` sinds ${sinds}` : ''} — pakket volgt zodra het rekenstation aan staat.`;
}
